import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import type { Product } from '@/types/product';

export interface QuoteItem {
  product: Product;
  quantity: number;
}

interface QuoteContextType {
  items: QuoteItem[];
  addToQuote: (product: Product) => void;
  removeFromQuote: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearQuote: () => void;
  isInQuote: (productId: string) => boolean;
  getItemQuantity: (productId: string) => number;
  totalItems: number;
  totalAmount: number;
}

const QuoteContext = createContext<QuoteContextType | undefined>(undefined);

const STORAGE_KEY = 'quote-items';

export function QuoteProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuthContext();
  const [items, setItems] = useState<QuoteItem[]>([]);

  // Load from localStorage on mount (only for authenticated users)
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const stored = localStorage.getItem(`${STORAGE_KEY}-${user.id}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setItems(parsed);
        } catch (e) {
          console.error('Failed to parse stored quote items:', e);
        }
      }
    } else {
      setItems([]);
    }
  }, [isAuthenticated, user?.id]);

  // Save to localStorage when items change
  useEffect(() => {
    if (isAuthenticated && user?.id && items.length > 0) {
      localStorage.setItem(`${STORAGE_KEY}-${user.id}`, JSON.stringify(items));
    } else if (isAuthenticated && user?.id && items.length === 0) {
      localStorage.removeItem(`${STORAGE_KEY}-${user.id}`);
    }
  }, [items, isAuthenticated, user?.id]);

  const addToQuote = (product: Product) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromQuote = (productId: string) => {
    setItems((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity < 1) {
      removeFromQuote(productId);
      return;
    }
    setItems((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearQuote = () => {
    setItems([]);
    if (user?.id) {
      localStorage.removeItem(`${STORAGE_KEY}-${user.id}`);
    }
  };

  const isInQuote = (productId: string) => {
    return items.some((item) => item.product.id === productId);
  };

  const getItemQuantity = (productId: string) => {
    return items.find((item) => item.product.id === productId)?.quantity || 0;
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const totalAmount = items.reduce((sum, item) => {
    const price = item.product.price_eur || 0;
    return sum + price * item.quantity;
  }, 0);

  return (
    <QuoteContext.Provider
      value={{
        items,
        addToQuote,
        removeFromQuote,
        updateQuantity,
        clearQuote,
        isInQuote,
        getItemQuantity,
        totalItems,
        totalAmount,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
}

export function useQuote() {
  const context = useContext(QuoteContext);
  if (context === undefined) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
}
